// Admin-side panel for the versioned draft review loop.
// CA uploads a draft → user approves or requests changes → CA uploads next.
// Old revisions are preserved and remain previewable + downloadable.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  Eye,
  FileText,
  History,
  MessageSquareWarning,
  RefreshCw,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import API from "../../lib/api";
import OfficePreview, { isOfficePreviewable } from "../OfficePreview";

const STATUS_META = {
  pending_review: {
    label: "Awaiting Client Review",
    icon: Clock,
    pill:  "bg-amber-100 text-amber-700",
    accent:"border-amber-200",
  },
  approved: {
    label: "Approved by Client",
    icon: CheckCircle2,
    pill:  "bg-green-100 text-green-700",
    accent:"border-green-200",
  },
  changes_requested: {
    label: "Changes Requested",
    icon: MessageSquareWarning,
    pill:  "bg-red-100 text-red-600",
    accent:"border-red-200",
  },
};

function formatBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function timeAgo(ts) {
  if (!ts) return "";
  const sec = Math.max(1, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  if (sec < 60)     return `${sec}s ago`;
  if (sec < 3600)   return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400)  return `${Math.floor(sec / 3600)} h ago`;
  return new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

// Authenticated preview / download for /admin/documents/:id
async function fetchAdminDoc(docId, { inline = false } = {}) {
  const url = `/admin/documents/${docId}/download${inline ? "?inline=1" : ""}`;
  const { data } = await API.get(url, { responseType: "blob" });
  return URL.createObjectURL(data);
}

export default function DraftRevisionsPanel({ orderId, stepNumber, stepName, onRefresh }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Multi-file picker. Each file becomes its own revision (v1, v2, v3…) so
  // the version-history audit stays one-row-per-file and the client can
  // approve / request changes per file. 10 MB per file (enforced server-side).
  const [files, setFiles] = useState([]);
  const [caNote, setCaNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: null });
  const [errors, setErrors] = useState([]);

  // In-window preview modal: { url, name, mime }
  const [preview, setPreview] = useState(null);
  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await API.get(
        `/admin/orders/${orderId}/steps/${stepNumber}/revisions`
      );
      setRevisions(data.revisions || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load revisions");
    }
    setLoading(false);
  }, [orderId, stepNumber]);

  useEffect(() => { load(); }, [load]);

  const latest = revisions[0] || null;
  const latestApproved = latest?.status === "approved";
  const latestChangesRequested = latest?.status === "changes_requested";

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    const over10mb = picked.filter((f) => f.size > 10 * 1024 * 1024).map((f) => f.name);
    if (over10mb.length > 0) {
      alert(`These files exceed 10 MB and will be rejected:\n${over10mb.join("\n")}`);
    }
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
      const next = [...prev];
      for (const f of picked) {
        const key = `${f.name}|${f.size}`;
        if (!seen.has(key) && next.length < 20) {
          next.push(f);
          seen.add(key);
        }
      }
      return next;
    });
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onUpload = async () => {
    if (files.length === 0 || uploading) return;
    setUploading(true);
    setErrors([]);
    setProgress({ done: 0, total: files.length, current: files[0]?.name });

    // One batch_id shared by every file in this upload click. The client
    // will see them grouped as a single approval unit.
    const batchId = (typeof window !== "undefined" && window.crypto?.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ done: i, total: files.length, current: f.name });
      try {
        const form = new FormData();
        form.append("deliverable", f);
        if (caNote.trim()) form.append("ca_note", caNote.trim().slice(0, 500));
        form.append("batch_id", batchId);
        await API.post(
          `/admin/orders/${orderId}/steps/${stepNumber}/draft`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } catch (e) {
        failed.push({ name: f.name, message: e.response?.data?.detail || "Upload failed" });
      }
    }

    setProgress({ done: files.length, total: files.length, current: null });
    const failedNames = new Set(failed.map((x) => x.name));
    setFiles((prev) => prev.filter((f) => failedNames.has(f.name)));
    setErrors(failed);
    if (failed.length === 0) setCaNote("");
    await load();
    if (onRefresh) await onRefresh();
    setUploading(false);
  };

  const headerStatus = useMemo(() => {
    if (!latest) return { label: "No draft uploaded yet", tone: "text-[#6B7280]" };
    const meta = STATUS_META[latest.status];
    return { label: meta.label, tone: meta.pill };
  }, [latest]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-[#6C63FF]" />
          <h4 className="text-sm font-heading font-bold text-[#1A1A2E]">
            {stepName} — Draft Review
          </h4>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-[11px] font-body text-[#6B7280] hover:text-[#6C63FF] px-2 py-1 rounded-lg hover:bg-white/60"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      {/* Upload card */}
      {latestApproved ? (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-heading font-semibold text-green-800">
              Client has approved the latest draft.
            </p>
            <p className="text-xs font-body text-green-700 mt-1">
              The step is closed. To upload a new revision, reopen the step first.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E6F8] p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-heading font-bold text-[#1A1A2E] uppercase tracking-wide">
              Upload {latest ? `Revisions from v${latest.version + 1}` : "Draft v1+"}
            </p>
            <div className="flex items-center gap-2">
              {files.length > 0 && (
                <span className="text-[10px] font-body font-semibold text-[#6C63FF] bg-[#EEEDFC] px-2 py-0.5 rounded-full">
                  {files.length} queued
                </span>
              )}
              {latestChangesRequested && latest.client_message && (
                <span className="text-[10px] font-body text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                  Client requested changes
                </span>
              )}
            </div>
          </div>

          {latestChangesRequested && latest.client_message && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-red-700 mb-1">
                Client's message
              </p>
              <p className="text-sm text-red-700 font-body italic leading-snug">
                "{latest.client_message}"
              </p>
              {latest.client_action_at && (
                <p className="text-[10px] text-red-500 font-body mt-1.5">
                  {timeAgo(latest.client_action_at)}
                </p>
              )}
            </div>
          )}

          <label
            htmlFor={`draft-file-input-${stepNumber}`}
            className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-[#E8E6F8] hover:border-[#6C63FF]/40 rounded-xl py-6 cursor-pointer transition-colors bg-[#FAFAFE]"
          >
            <Upload className="h-5 w-5 text-[#6C63FF]" />
            <p className="text-xs font-body text-[#1A1A2E]">
              <span className="font-heading font-bold text-[#6C63FF]">Click to add files</span>
              {" "}or drag &amp; drop
            </p>
            <p className="text-[10px] text-[#9CA3AF] font-body text-center px-3">
              Any file type · max 10 MB each · up to 20 files. Each file becomes a separate version the client can review.
            </p>
            <input
              id={`draft-file-input-${stepNumber}`}
              type="file"
              multiple
              onChange={onPickFiles}
              disabled={uploading}
              className="hidden"
            />
          </label>

          {/* Queued files */}
          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((f, i) => {
                const oversized = f.size > 10 * 1024 * 1024;
                return (
                  <div
                    key={`${f.name}-${f.size}-${i}`}
                    className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 ${
                      oversized
                        ? "bg-red-50 border-red-200"
                        : "bg-[#FAFAFE] border-[#E8E6F8]"
                    }`}
                  >
                    <FileText className={`h-3.5 w-3.5 flex-shrink-0 ${oversized ? "text-red-500" : "text-[#6C63FF]"}`} />
                    <span className="text-xs font-body text-[#1A1A2E] truncate flex-1 min-w-0">
                      {f.name}
                    </span>
                    <span className={`text-[10px] font-body whitespace-nowrap ${oversized ? "text-red-600 font-semibold" : "text-[#9CA3AF]"}`}>
                      {formatBytes(f.size)}{oversized ? " · over 10 MB" : ""}
                    </span>
                    <button
                      onClick={() => removeFile(i)}
                      disabled={uploading}
                      className="text-[#9CA3AF] hover:text-red-500 disabled:opacity-30"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <textarea
            value={caNote}
            onChange={(e) => setCaNote(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="Optional caption applied to every file in this batch (e.g. 'Updated capital gains schedule per your feedback')."
            disabled={uploading}
            className="w-full text-xs font-body border border-[#E8E6F8] rounded-lg p-2.5 bg-[#F5F4FB] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50 resize-none disabled:opacity-60"
          />

          {/* Per-batch progress */}
          {uploading && progress.total > 0 && (
            <div>
              <div className="flex items-center justify-between text-[11px] font-body text-[#6B7280] mb-1">
                <span className="truncate pr-2">
                  Uploading <span className="font-mono">{progress.current}</span> · {progress.done + 1} of {progress.total}
                </span>
                <span className="font-heading font-bold text-[#6C63FF]">
                  {Math.round((progress.done / progress.total) * 100)}%
                </span>
              </div>
              <div className="w-full h-1.5 bg-[#F5F4FB] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] transition-all"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Per-file errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 space-y-1">
              <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-red-700">
                {errors.length} upload{errors.length === 1 ? "" : "s"} failed — fix and retry
              </p>
              {errors.map((er, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[11px] font-body text-red-600">
                  <XCircle className="h-3 w-3 flex-shrink-0" />
                  <span className="font-mono truncate">{er.name}</span>
                  <span className="text-red-500/80">— {er.message}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[10px] text-[#9CA3AF] font-body">
              {caNote.length}/500 chars
            </span>
            <button
              onClick={onUpload}
              disabled={files.length === 0 || uploading || files.some((f) => f.size > 10 * 1024 * 1024)}
              className="inline-flex items-center gap-1.5 text-xs font-body font-semibold bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Upload className="h-3.5 w-3.5" />
              {uploading
                ? `Uploading ${progress.done + 1}/${progress.total}…`
                : files.length > 1
                  ? `Upload ${files.length} files`
                  : "Upload Draft"}
            </button>
          </div>
        </div>
      )}

      {/* Revision history */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <History className="h-3.5 w-3.5 text-[#6C63FF]" />
          <p className="text-xs font-heading font-bold text-[#1A1A2E] uppercase tracking-wide">
            Version History
          </p>
          <span className={`ml-auto text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${headerStatus.tone}`}>
            {headerStatus.label}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-4 text-center bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="h-4 w-4 text-red-500 mx-auto mb-1" />
            <p className="text-xs text-red-600 font-body">{error}</p>
          </div>
        ) : revisions.length === 0 ? (
          <p className="text-center text-xs text-[#9CA3AF] font-body py-8">
            No drafts uploaded yet — your first upload will become version 1.
          </p>
        ) : (
          <div className="space-y-2">
            {revisions.map((r) => (
              <RevisionRow key={r.id} rev={r} onPreview={setPreview} />
            ))}
          </div>
        )}
      </div>

      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// Inline preview modal — shows images / PDFs / iframable docs in-window.
function PreviewModal({ preview, onClose }) {
  const { url, mime, name } = preview;
  const isImage = mime?.startsWith("image/");
  const isPdf   = mime === "application/pdf";
  const isOffice = isOfficePreviewable({ mime, name });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E6F8]">
          <p className="text-sm font-heading font-bold text-[#1A1A2E] truncate pr-4">{name}</p>
          <div className="flex items-center gap-2">
            <a
              href={url}
              download={name}
              className="inline-flex items-center gap-1 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-2.5 py-1.5"
            >
              <Download className="h-3 w-3" /> Download
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-[#F5F4FB] text-[#6B7280]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[#F5F4FB]">
          {isImage ? (
            <img src={url} alt={name} className="max-w-full max-h-full mx-auto" />
          ) : isPdf ? (
            <iframe src={url} title={name} className="w-full h-[80vh] border-0" />
          ) : isOffice ? (
            <OfficePreview url={url} mime={mime} name={name} />
          ) : (
            <div className="p-8 text-center text-sm text-[#6B7280] font-body">
              <p className="mb-3">This file type can't be previewed in-window.</p>
              <a
                href={url}
                download={name}
                className="inline-flex items-center gap-1.5 text-sm font-body font-semibold text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-3 py-2"
              >
                <Download className="h-4 w-4" /> Download to view
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RevisionRow({ rev, onPreview: openPreview }) {
  const meta = STATUS_META[rev.status] || STATUS_META.pending_review;
  const Icon = meta.icon;
  const [open, setOpen] = useState(rev.status !== "approved");
  const [busy, setBusy] = useState(null); // 'preview' | 'download'

  const onPreview = async () => {
    setBusy("preview");
    try {
      const url = await fetchAdminDoc(rev.file.id, { inline: true });
      openPreview({ url, name: rev.file.original_name, mime: rev.file.mime });
    } catch (e) {
      alert(e.response?.data?.detail || "Preview failed");
    }
    setBusy(null);
  };

  const onDownload = async () => {
    setBusy("download");
    try {
      const url = await fetchAdminDoc(rev.file.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = rev.file.original_name || `draft-v${rev.version}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(e.response?.data?.detail || "Download failed");
    }
    setBusy(null);
  };

  return (
    <div className={`bg-white border ${meta.accent} rounded-xl p-3`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 text-left"
      >
        <div className="w-9 h-9 rounded-full bg-[#F5F4FB] flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-mono font-bold text-[#6C63FF]">v{rev.version}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${meta.pill}`}>
              <Icon className="h-3 w-3" />
              {meta.label}
            </span>
            <span className="text-[10px] text-[#9CA3AF] font-body">
              uploaded by {rev.uploaded_by_name || "CA"} · {timeAgo(rev.created_at)}
            </span>
          </div>
          <p className="text-xs font-body text-[#1A1A2E] mt-0.5 truncate">
            {rev.file.original_name} · <span className="text-[#9CA3AF]">{formatBytes(rev.file.size)}</span>
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 pl-12 space-y-2">
          {rev.ca_note && (
            <p className="text-[11px] text-[#4A4A60] font-body italic bg-[#FAFAFE] border border-[#E8E6F8] rounded-lg px-3 py-2">
              "{rev.ca_note}"
            </p>
          )}
          {rev.client_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-red-700 mb-1">
                Client requested
              </p>
              <p className="text-[11px] text-red-700 font-body italic">
                "{rev.client_message}"
              </p>
              {rev.client_action_at && (
                <p className="text-[10px] text-red-500 font-body mt-1">
                  on {new Date(rev.client_action_at).toLocaleString("en-IN")}
                </p>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={onPreview}
              disabled={busy === "preview"}
              className="inline-flex items-center gap-1 text-[11px] font-body text-[#6C63FF] hover:bg-[#EEEDFC] border border-[#6C63FF]/30 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-60"
            >
              <Eye className="h-3 w-3" />
              {busy === "preview" ? "Loading…" : "Preview"}
            </button>
            <button
              onClick={onDownload}
              disabled={busy === "download"}
              className="inline-flex items-center gap-1 text-[11px] font-body text-[#6C63FF] hover:bg-[#EEEDFC] border border-[#6C63FF]/30 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-60"
            >
              <Download className="h-3 w-3" />
              {busy === "download" ? "Loading…" : "Download"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
